// src/utils/msCategoryColors.js
//
// Maps BRIO calendar event types to Microsoft Outlook category names and
// preset colors. Outlook category colors are not free-form hex — every
// mailbox keeps a "master category list" where each named category is
// bound to one of 25 named presets (preset0..preset24). When we push an
// event to Outlook with a categories: [<name>] field, Outlook renders the
// event in whatever color the master category list maps that name to.
//
// Flow:
//   1. seedMsMasterCategories() upserts the BRIO categories on the
//      authenticated mailbox (one-time admin action on the shared
//      projects@ mailbox).
//   2. buildMsEventPayload (App.js) attaches categories: [name] to every
//      pushed event using getCategoryNameForEventType().
//
// Preset palette reference (closest match per BRIO hex; tweak freely):
//   preset0=Red       preset1=Orange   preset2=Brown    preset3=Yellow
//   preset4=Green     preset5=Teal     preset6=Olive    preset7=Blue
//   preset8=Purple    preset9=Cranberry preset10=Steel   preset11=DarkSteel
//   preset12=Gray     preset13=DarkGray preset14=Black   preset15=DarkRed
//   preset16=DarkOrange preset17=DarkBrown preset18=DarkYellow
//   preset19=DarkGreen preset20=DarkTeal preset21=DarkOlive
//   preset22=DarkBlue preset23=DarkPurple preset24=DarkCranberry

export const BRIO_MS_CATEGORY_MAP = {
  holidays:             { name: 'BRIO — Public Holidays',  presetColor: 'preset0'  }, // red
  leaves:               { name: 'BRIO — Team Leaves',      presetColor: 'preset2'  }, // brown
  pendingLeaves:        { name: 'BRIO — Pending Leaves',   presetColor: 'preset6'  }, // olive
  milestones:           { name: 'BRIO — Milestones',       presetColor: 'preset4'  }, // green
  invoicing:            { name: 'BRIO — Invoicing',        presetColor: 'preset10' }, // steel
  events:               { name: 'BRIO — General Events',   presetColor: 'preset5'  }, // teal
  anniversaries:        { name: 'BRIO — Anniversaries',    presetColor: 'preset8'  }, // purple
  birthdays:            { name: 'BRIO — Birthdays',        presetColor: 'preset23' }, // dark purple
  clientBirthdays:      { name: 'BRIO — Client Birthdays', presetColor: 'preset9'  }, // cranberry
  clientAnniversaries:  { name: 'BRIO — Client Anniversaries', presetColor: 'preset24' }, // dark cranberry
  msCalendar:           { name: 'BRIO — General Events',   presetColor: 'preset5'  }, // alias → events
};

export const getCategoryNameForEventType = (eventType) => {
  const entry = BRIO_MS_CATEGORY_MAP[eventType];
  return entry ? entry.name : null;
};

// Maps each BRIO event type to an MS Graph calendar `color` preset. Unlike
// master categories, Graph calendars only accept a 10-color palette (no
// purple), so the BRIO hue families collapse a little — every mapping is
// the closest available preset.
//
// Accepted values per Microsoft docs:
//   auto | lightBlue | lightGreen | lightOrange | lightGray |
//   lightYellow | lightTeal | lightPink | lightBrown | lightRed
export const BRIO_MS_CALENDAR_COLOR_MAP = {
  holidays:            'lightRed',    // muted red
  leaves:              'lightBrown',  // tan / gold
  pendingLeaves:       'lightYellow', // ochre
  milestones:          'lightGreen',  // forest green
  invoicing:           'lightBlue',   // blue steel
  events:              'lightTeal',   // teal
  anniversaries:       'lightOrange', // no purple in palette — warm distinct
  birthdays:           'lightPink'    // lavender / pink family
};

export const getCalendarColorForEventType = (eventType) => {
  return BRIO_MS_CALENDAR_COLOR_MAP[eventType] || null;
};

// Seed (create or update) BRIO master categories on the authenticated
// mailbox. `fetchFn` must be a fetch-like function that talks to MS Graph
// with the user's access token already attached (App.js's msGraphFetch).
//
// Returns { created, updated, unchanged, failed, errors } so the caller
// can report progress to the admin.
export async function seedMsMasterCategories(fetchFn) {
  const result = { created: 0, updated: 0, unchanged: 0, failed: 0, errors: [] };

  const listResp = await fetchFn('https://graph.microsoft.com/v1.0/me/outlook/masterCategories');
  if (!listResp.ok) {
    const txt = await listResp.text().catch(() => '');
    throw new Error(`Failed to list master categories (${listResp.status}): ${txt}`);
  }
  const listJson = await listResp.json();
  const existing = new Map(
    (listJson.value || []).map((c) => [c.displayName, c])
  );

  // Deduplicate by name so aliases (e.g. msCalendar → events) don't double-post
  const seen = new Set();
  const targets = Object.values(BRIO_MS_CATEGORY_MAP).filter((t) => {
    if (seen.has(t.name)) return false;
    seen.add(t.name);
    return true;
  });

  for (const target of targets) {
    try {
      const found = existing.get(target.name);
      if (!found) {
        const createResp = await fetchFn('https://graph.microsoft.com/v1.0/me/outlook/masterCategories', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ displayName: target.name, color: target.presetColor })
        });
        if (!createResp.ok) {
          const txt = await createResp.text().catch(() => '');
          throw new Error(`POST ${target.name} failed (${createResp.status}): ${txt}`);
        }
        result.created += 1;
      } else if (found.color !== target.presetColor) {
        const patchResp = await fetchFn(
          `https://graph.microsoft.com/v1.0/me/outlook/masterCategories/${encodeURIComponent(found.id)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ color: target.presetColor })
          }
        );
        if (!patchResp.ok) {
          const txt = await patchResp.text().catch(() => '');
          throw new Error(`PATCH ${target.name} failed (${patchResp.status}): ${txt}`);
        }
        result.updated += 1;
      } else {
        result.unchanged += 1;
      }
    } catch (err) {
      result.failed += 1;
      result.errors.push({ name: target.name, message: err.message });
    }
  }

  return result;
}
